import {
  Banner,
  BlockStack,
  Button,
  Icon,
  Spinner,
  Text,
} from "@shopify/polaris";
import {
  AlertCircleIcon,
  CheckCircleIcon,
  ConnectIcon,
  ExternalIcon,
  RefreshIcon,
} from "@shopify/polaris-icons";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ColoredGoogleIcon from "../icons/google";

function GoogleShoppingCard() {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(location.search);
  const paramValue = urlParams.get("shop");
  const accountEmail = integration?.email || "";

  const fetchIntegration = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/google/auth/info?shop=${paramValue}`,
      );
      const data = await response.json();
      if (data?.data?.id) {
        setIntegration(data.data);
        setConnected(true);
      } else {
        setIntegration(null);
        setConnected(false);
      }
    } catch (error) {
      console.error("Error fetching integration:", error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/google/auth/account/statuses?shop=${paramValue}`,
      );
      const data = await response.json();
      setAccountStatus(data?.accountStatus);
    } catch (error) {
      console.error("Error fetching account status:", error);
    }
  };

  useEffect(() => {
    fetchIntegration();
    fetchAccountStatus();
  }, []);

  const handleConnect = useCallback(() => {
    const popup = window.open(
      `${process.env.REACT_APP_API_URL}/api/google/auth?shop=${paramValue}`,
      "googleAuthPopup",
      "width=600,height=700",
    );

    const popupTimer = setInterval(() => {
      if (popup?.closed) {
        clearInterval(popupTimer);
        fetchIntegration();
        fetchAccountStatus();
      }
    }, 500);
  }, [paramValue]);

  const changeAccRequest = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to reconnect Google Merchant Center? You need to go through the integration process again if you proceed further.",
    );
    if (!confirmed) return;

    setActionLoading(true);
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/google/auth/delete?shop=${paramValue}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );
      navigate(`/settings?shop=${paramValue}`);
    } catch (error) {
      console.error("Error deleting account:", error);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="app-surface-card">
        <div
          className="app-surface-card__body"
          style={{ textAlign: "center", padding: 48 }}
        >
          <Spinner accessibilityLabel="Loading Google connection" size="large" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="integration-hero">
        <div className="integration-hero__panel">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ColoredGoogleIcon />
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                opacity: 0.85,
              }}
            >
              Google Merchant Center
            </span>
          </div>
          <h1>
            {connected
              ? "Your Google account is connected"
              : "Connect Google to start syncing"}
          </h1>
          <p>
            {connected
              ? "Manage the linked Merchant Center account, claim status, and switch IDs when needed."
              : "Authorize Google so product feeds, diagnostics, and Merchant Center updates can run from this app."}
          </p>
          <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
            {!connected ? (
              <Button
                variant="primary"
                icon={ConnectIcon}
                onClick={handleConnect}
              >
                Connect Google
              </Button>
            ) : (
              <>
                <Button
                  icon={RefreshIcon}
                  loading={actionLoading}
                  onClick={changeAccRequest}
                >
                  Change account
                </Button>
                <Button
                  icon={ExternalIcon}
                  onClick={() =>
                    navigate(`/merchant-selection?shop=${paramValue}`, {
                      state: { merchantId: integration?.id },
                    })
                  }
                >
                  Change Merchant ID
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="integration-stat-grid">
          <div className="integration-stat">
            <div className="integration-stat__label">Status</div>
            <div
              className="integration-stat__value"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <Icon
                source={connected ? CheckCircleIcon : AlertCircleIcon}
                tone={connected ? "success" : "critical"}
              />
              {connected ? "Connected" : "Not connected"}
            </div>
          </div>
          <div className="integration-stat">
            <div className="integration-stat__label">Google account</div>
            <div className="integration-stat__value">
              {accountEmail || "—"}
            </div>
          </div>
          <div className="integration-stat">
            <div className="integration-stat__label">Website claim</div>
            <div
              className="integration-stat__value"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              {connected ? (
                accountStatus?.websiteClaimed ? (
                  <>
                    <Icon source={CheckCircleIcon} tone="success" />
                    Claimed
                  </>
                ) : (
                  <>
                    <Icon source={AlertCircleIcon} tone="critical" />
                    Unclaimed
                  </>
                )
              ) : (
                "—"
              )}
            </div>
          </div>
        </div>
      </div>

      {!connected ? (
        <div className="app-surface-card">
          <div className="app-surface-card__body">
            <Banner tone="info">
              By clicking <strong>Connect Google</strong>, you authorize this
              app to access your Google Merchant Center account for product
              feed sync.
            </Banner>
            <div style={{ marginTop: 16 }}>
              <BlockStack gap="200">
                <Text as="p" tone="subdued">
                  After connecting you will:
                </Text>
                <ul className="howto-step__list">
                  <li>Select the correct Merchant Center account</li>
                  <li>Confirm the website claim status</li>
                  <li>Start syncing Shopify products to Google</li>
                </ul>
              </BlockStack>
            </div>
          </div>
        </div>
      ) : (
        <div className="app-surface-card">
          <div className="app-surface-card__body">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <span className="app-icon-tile app-icon-tile--success">
                <Icon source={ConnectIcon} tone="base" />
              </span>
              <div>
                <Text variant="headingLg" as="h2">
                  Connection details
                </Text>
                <Text as="p" tone="subdued">
                  Linked Merchant Center account used for feed submissions.
                </Text>
              </div>
            </div>

            <div className="integration-row">
              <div className="integration-row__label">Status</div>
              <div>
                <span className="app-chip app-chip--success">
                  <Icon source={CheckCircleIcon} tone="success" />
                  Connected
                </span>
              </div>
            </div>
            <div className="integration-row">
              <div className="integration-row__label">Google Account</div>
              <div>
                <Text as="span">{accountEmail || "—"} </Text>
                <span className="integration-link" onClick={changeAccRequest}>
                  Change Account
                </span>
              </div>
            </div>
            <div className="integration-row">
              <div className="integration-row__label">
                Google Merchant Center ID
              </div>
              <div>
                <Text as="span">{integration?.id || "—"} </Text>
                <span
                  className="integration-link"
                  onClick={() =>
                    navigate(`/merchant-selection?shop=${paramValue}`, {
                      state: { merchantId: integration?.id },
                    })
                  }
                >
                  Change Merchant ID
                </span>
              </div>
            </div>
            <div className="integration-row">
              <div className="integration-row__label">
                Merchant Center Website
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <Text fontWeight="bold">{integration?.websiteUrl || "—"}</Text>
                {accountStatus?.websiteClaimed ? (
                  <span className="app-chip app-chip--success">
                    <Icon source={CheckCircleIcon} tone="success" />
                    Claimed
                  </span>
                ) : (
                  <span className="app-chip app-chip--warn">
                    <Icon source={AlertCircleIcon} tone="critical" />
                    Unclaimed
                  </span>
                )}
              </div>
            </div>
            <div className="integration-row">
              <div className="integration-row__label">Primary Domain</div>
              <div>
                <Text as="span">{integration?.websiteUrl || "—"}</Text>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GoogleShoppingCard;
